import { useId, useRef, useState, type FormEvent } from 'react';
import {
  DRINK_KIND_LABELS,
  DRINK_KINDS,
  MAX_BOTTLE_QUANTITY,
  MAX_BOTTLE_TEXT_LENGTH,
  type BottleDetails,
  type DrinkKind,
} from '../../shared/api';
import { errorMessage } from '../lib/api';
import { SERVE_PRESETS } from '../lib/bottles';

interface BottleFormProps {
  initial: BottleDetails;
  submitLabel: string;
  onSubmit: (details: BottleDetails) => Promise<void>;
  /** A second way to submit the same details, e.g. "Add & open" next to "Add to bar". */
  alternative?: { label: string; onSubmit: (details: BottleDetails) => Promise<void> };
  onCancel?: () => void;
}

const CUSTOM = 'custom';

/** A blank is null; anything that isn't a number of at least `min` is undefined (invalid). */
function numberOrNull(value: string, min: number, integer: boolean): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const number = Number(trimmed);
  if (!Number.isFinite(number) || number < min || (integer && !Number.isInteger(number))) return undefined;
  return number;
}

/** The details of one bottle; numbers are kept as text while editing so partial input doesn't fight the user. */
export function BottleForm({ initial, submitLabel, onSubmit, alternative, onCancel }: BottleFormProps) {
  const [name, setName] = useState(initial.name);
  const [brand, setBrand] = useState(initial.brand ?? '');
  const [kind, setKind] = useState<DrinkKind>(initial.kind);
  const [variant, setVariant] = useState(initial.variant ?? '');
  const [sizeMl, setSizeMl] = useState(initial.sizeMl?.toString() ?? '');
  const [abv, setAbv] = useState(initial.abv?.toString() ?? '');
  const presetServe = SERVE_PRESETS.some((preset) => preset.ml === initial.serveMl);
  const [serve, setServe] = useState(presetServe ? String(initial.serveMl) : CUSTOM);
  const [customServe, setCustomServe] = useState(presetServe ? '' : (initial.serveMl?.toString() ?? ''));
  const [quantity, setQuantity] = useState(String(initial.quantity));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  // Which submit button was pressed; the alternative sets this just before the form submits.
  const useAlternativeRef = useRef(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submit = useAlternativeRef.current && alternative ? alternative.onSubmit : onSubmit;
    useAlternativeRef.current = false;
    const size = numberOrNull(sizeMl, 1, true);
    const strength = numberOrNull(abv, 0, false); // 0% for mixers
    const serveMl = serve === CUSTOM ? numberOrNull(customServe, 1, true) : serve === 'null' ? null : Number(serve);
    const units = Number(quantity.trim());
    if (!name.trim()) return setError('Give it a name.');
    if (size === undefined) return setError('Size must be a whole number of millilitres.');
    if (strength === undefined || (strength !== null && strength > 100)) return setError('ABV must be a percentage.');
    if (serveMl === undefined) return setError('A custom serve must be a whole number of millilitres.');
    if (!Number.isInteger(units) || units < 0 || units > MAX_BOTTLE_QUANTITY) {
      return setError(`How many must be a whole number up to ${MAX_BOTTLE_QUANTITY}.`);
    }
    setSaving(true);
    setError(null);
    try {
      await submit({
        barcode: initial.barcode,
        imageUrl: initial.imageUrl,
        wantMore: initial.wantMore,
        restockAt: initial.restockAt,
        name: name.trim(),
        brand: brand.trim() || null,
        kind,
        variant: variant.trim() || null,
        sizeMl: size,
        abv: strength,
        serveMl,
        quantity: units,
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="bottle-form" onSubmit={(event) => void handleSubmit(event)} noValidate>
      <label className="field field--wide">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={MAX_BOTTLE_TEXT_LENGTH} required autoFocus={!name} />
      </label>
      <label className="field">
        <span>Brand</span>
        <input value={brand} onChange={(e) => setBrand(e.target.value)} maxLength={MAX_BOTTLE_TEXT_LENGTH} />
      </label>
      <label className="field">
        <span>Kind</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as DrinkKind)}>
          {DRINK_KINDS.map((option) => (
            <option key={option} value={option}>
              {DRINK_KIND_LABELS[option]}
            </option>
          ))}
        </select>
      </label>
      <label className="field field--wide">
        <span>Vintage, batch or season</span>
        <input
          value={variant}
          onChange={(e) => setVariant(e.target.value)}
          maxLength={MAX_BOTTLE_TEXT_LENGTH}
          placeholder="e.g. 2019, Batch 12, Winter release"
        />
      </label>
      <label className="field">
        <span>Size (ml)</span>
        <input type="number" inputMode="numeric" min={1} value={sizeMl} onChange={(e) => setSizeMl(e.target.value)} />
      </label>
      <label className="field">
        <span>ABV (%)</span>
        <input type="number" inputMode="decimal" min={0} max={100} step="0.1" value={abv} onChange={(e) => setAbv(e.target.value)} />
      </label>
      <fieldset className="field field--wide">
        <legend id={`${id}-serve`}>One pour is…</legend>
        <div className="category-toggles" role="radiogroup" aria-labelledby={`${id}-serve`}>
          {SERVE_PRESETS.map((preset) => {
            const value = String(preset.ml);
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={serve === value}
                className={`category-toggle${serve === value ? ' is-selected' : ''}`}
                onClick={() => setServe(value)}
              >
                {preset.label}
              </button>
            );
          })}
          <button
            type="button"
            role="radio"
            aria-checked={serve === CUSTOM}
            className={`category-toggle${serve === CUSTOM ? ' is-selected' : ''}`}
            onClick={() => setServe(CUSTOM)}
          >
            Other
          </button>
        </div>
        {serve === CUSTOM && (
          <label className="field">
            <span>Pour size (ml)</span>
            <input type="number" inputMode="numeric" min={1} value={customServe} onChange={(e) => setCustomServe(e.target.value)} autoFocus />
          </label>
        )}
      </fieldset>
      <label className="field">
        <span>How many</span>
        <input type="number" inputMode="numeric" min={0} max={MAX_BOTTLE_QUANTITY} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
      </label>
      {initial.barcode && (
        <p className="field field--wide muted">
          Barcode <span className="mono">{initial.barcode}</span>
        </p>
      )}
      {error && (
        <p className="error-text field--wide" role="alert">
          {error}
        </p>
      )}
      <div className="actions field--wide">
        <button type="submit" className="btn btn--primary" disabled={saving}>
          {saving ? 'Saving…' : submitLabel}
        </button>
        {alternative && (
          <button type="submit" className="btn btn--primary" disabled={saving} onClick={() => (useAlternativeRef.current = true)}>
            {alternative.label}
          </button>
        )}
        {onCancel && (
          <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
